import { createInitialLastInsertedField, useSigningState } from '@modusign/react-signing/setup';
import { Host, SignerToReference } from '@modusign/react-signing/types';
import { useDispatch, useSelector } from 'react-redux';
import { v4 } from 'uuid';

import { checkIsInOrder } from '@app/features/apply-react-signing/libs/checkIsInOrder';
import { toSigningStateForSetup } from '@app/features/apply-react-signing/libs/signing-mapper';
import { usePDF } from '@app/hooks/usePDF';
import pushDataLayer from '@app/libs/analytics/googleTagManager';
import workspaceService from '@app/libs/workspaceService';
import {
  contractSetupLabelIdsChange,
  contractSetupRequiredVerificationChange,
} from '@app/store/contract-setup/actions';
import { ContractSetupVerificationMethodMap } from '@app/store/contract-setup/ContractSetupVerificationMethodMap';
import { getFileFromURL } from '@app/store/transport-layer/files';
import { getTemplateById } from '@app/store/transport-layer/template';
import { copyTemplateRequesterAttachments } from '@app/store/transport-layer/template/templates/attachments/copy';
import { postDocumentUpload } from '@app/store/transport-layer/upload/document/upload';
import { getUser } from '@app/store/user/selectors';

export function useLoadSigningStateByTemplate() {
  const dispatch = useDispatch();
  const { user } = useSelector(getUser);

  const { initialize } = useSigningState();
  const { setPDF, resetPDF } = usePDF();

  async function load({ templateId }: { templateId: string }) {
    resetPDF();
    const workspaceId = workspaceService.getWorkspaceId();
    const [attachmentCopyResponse, template] = await Promise.all([
      copyTemplateRequesterAttachments(workspaceId, templateId),
      getTemplateById(workspaceId, templateId),
    ]);

    const { url: newUrl, fileId }: { fileId: string; url: string } = await postDocumentUpload({
      url: template.file.url,
    });

    const { data: pdf } = await getFileFromURL(newUrl);
    setPDF(pdf);

    const requiredVerificationMap: ContractSetupVerificationMethodMap[] =
      template.verifications.map((verification) => {
        const mappedVerificationMethods = verification.methods.flatMap((method) => method.type);
        return { [verification.participant.dataLabel]: mappedVerificationMethods };
      });

    dispatch(contractSetupRequiredVerificationChange({ verifications: requiredVerificationMap }));

    const state = toSigningStateForSetup(
      attachmentCopyResponse.attachments,
      template.participants,
      template.fields,
      template.fieldGroups,
      []
    );

    const inPersonSigners = state.participants.filter(
      (p): p is SignerToReference =>
        p.type === 'SIGNER' && p.signingMethod === 'IN_PERSON' && !p.host
    );

    const currentUserHost: Host = {
      type: 'HOST',
      id: v4(),
      name: user.name ?? '',
      contact: user.email ?? '',
      dataLabel: v4(),
      locale: 'ko',
    };

    if (inPersonSigners.length > 0) {
      state.participants.push(currentUserHost);
    }

    inPersonSigners.forEach((participant) => {
      participant.host = {
        participantId: currentUserHost.id,
      };
    });

    dispatch(
      contractSetupLabelIdsChange({
        labelIds: template.labelIds,
      })
    );

    initialize({
      attachments: state.attachments,
      participants: state.participants,
      fields: state.fields,
      verifications: state.verifications,
      document: {
        pdfFile: {
          url: newUrl,
          id: fileId,
        },
        pdfPassword: null,
        title: template.title,
      },
      ui: {
        isInOrder: checkIsInOrder(state.participants),
        insertingField: null,
        lastInsertedField: createInitialLastInsertedField(),
      },
    });

    pushDataLayer({ document: undefined });
    pushDataLayer({
      event: 'documentInitialized',
      document: {
        documentSource: 'template',
        startedAt: Date.now(),
      },
    });
  }

  return {
    load,
  };
}
